import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { apiGet } from '../composables/apiGet'
import { useAsyncResource } from '../composables/useAsyncResource'

// Per-week, per-model token + cost breakdown for the /usage dashboard
// Mirrors the JSON shape produced by `Api::V1::UsageController#index`:
// historical weeks read from frozen `UsageReport` rows; the current
// week is computed live so the "this week so far" callout reflects
// in-flight runs without waiting for Monday's cron.
//
// Read-only — no channel broadcasts (per-run rows aren't subscribed),
// no in-place mutation. Callers refresh by calling `fetch()` again
// (e.g., on route entry).

export interface UsageModelRow {
  model: string
  input_tokens: number
  output_tokens: number
  cache_read_tokens: number
  cache_creation_tokens: number
  estimated_cost_cents: number
}

// Per-operation cost breakdown: which of ingest / lint / synthesis / cv
// drove the week's spend. Present only on the in-progress (current) week —
// the backend derives it live from run rows and doesn't compute it for
// frozen historical weeks. Same token+cost shape as a model row, keyed by
// `operation` instead of `model`.
export interface UsageOperationRow {
  operation: string
  input_tokens: number
  output_tokens: number
  cache_read_tokens: number
  cache_creation_tokens: number
  estimated_cost_cents: number
}

export interface UsageWeek {
  week_start: string // ISO date "YYYY-MM-DD"
  in_progress: boolean
  models: UsageModelRow[]
  operations?: UsageOperationRow[]
  total_cost_cents: number
}

interface UsagePayload {
  weeks: UsageWeek[]
}

// Available Anthropic models + maintained pricing for the "Available models"
// section. Mirrors `Api::V1::UsageController#models`: the live
// Models API joined against the app's price table, newest-first. `pricing`
// is null when we don't have a maintained price for that model (UI shows
// "—"); `tier`/`in_use` say whether the app currently wires the model into
// an effort tier.
// `long_context` is the second rate card some models (Haiku 5.5) charge
// once a prompt exceeds `threshold_tokens`.
export interface RateCard {
  input_cents: number
  output_cents: number
  cache_read_cents: number
  cache_write_cents: number
}

export interface ModelPricing extends RateCard {
  long_context?: RateCard & { threshold_tokens: number }
}

export interface AvailableModel {
  id: string
  display_name: string | null
  context_window: number | null
  in_use: boolean
  tier: string | null
  pricing: ModelPricing | null
}

interface ModelsPayload {
  models: AvailableModel[]
  stale: boolean
}

export const useUsageStore = defineStore('usage', () => {
  // --- weeks resource ---

  // weekCount drives the URL inside the fetcher so the same resource
  // instance handles different time windows without re-creating it.
  const weekCount = ref(8)

  // Fetcher returns the weeks array directly so `data` aliases to `weeks`
  // — a writable ref compatible with $patch in view specs (same pattern as
  // lint_notes returning `data: notes`).
  const {
    data: weeks,
    loaded,
    loading,
    error: loadError,
    refresh: fetchWeeks,
  } = useAsyncResource<UsageWeek[]>(
    async () => {
      const payload = await apiGet<UsagePayload>(`/api/v1/usage?weeks=${weekCount.value}`, {
        failureLabel: 'Failed to load usage data',
      })
      return payload.weeks
    },
    { initialValue: [], onError: () => {} },
  )

  // The current (in-flight) week — by convention the first element
  // returned by the API. Exposed as a separate getter so the
  // "this week so far" callout can read it without indexing into
  // weeks[0] at every template site.
  const currentWeek = computed<UsageWeek | null>(() => {
    const first = weeks.value[0]
    return first?.in_progress ? first : null
  })

  // Weeks strictly before the current one. The historical strip the
  // dashboard renders below the callout.
  const pastWeeks = computed<UsageWeek[]>(() => weeks.value.filter((w) => !w.in_progress))

  async function fetch(count = 8) {
    weekCount.value = count
    await fetchWeeks()
  }

  // --- models resource ---
  // Separate from the weekly history: its own ~24h server-side cache
  // and a `stale` flag; a catalog hiccup never blanks the spend view.
  //
  // `modelsStale` is a separate writable ref (not computed from the
  // payload) so view specs can set it via $patch — same pattern as
  // lint_notes updating `openCount` as a fetcher side-effect.

  const modelsStale = ref(false)

  const {
    data: models,
    loaded: modelsLoaded,
    loading: modelsLoading,
    error: modelsError,
    refresh: fetchModels,
  } = useAsyncResource<AvailableModel[]>(
    async () => {
      const payload = await apiGet<ModelsPayload>('/api/v1/usage/models', {
        failureLabel: 'Failed to load available models',
      })
      modelsStale.value = payload.stale
      return payload.models
    },
    { initialValue: [], onError: () => {} },
  )

  return {
    weeks,
    loaded,
    loadError,
    loading,
    currentWeek,
    pastWeeks,
    fetch,
    models,
    modelsStale,
    modelsLoaded,
    modelsError,
    modelsLoading,
    fetchModels,
  }
})
